import { getCityType } from './city-types';

export type PricingTier = 'tier1' | 'tier2' | 'tier3';

export const PRICING_TIER_MAP: Record<string, PricingTier> = {
  'malaga': 'tier1',
  'marbella': 'tier1', 'fuengirola': 'tier1', 'torremolinos': 'tier1',
  'benalmadena': 'tier1', 'mijas': 'tier1', 'estepona': 'tier1',
  'velez-malaga': 'tier1', 'rincon-de-la-victoria': 'tier1',
  'benahavis': 'tier1', 'san-pedro-de-alcantara': 'tier1',
  'cartama': 'tier2', 'alhaurin-de-la-torre': 'tier2', 'alhaurin-el-grande': 'tier2',
  'antequera': 'tier2', 'coin': 'tier2', 'nerja': 'tier2', 'alora': 'tier2', 'ronda': 'tier2',
  'axarquia': 'tier3',
  'valle-del-guadalhorce': 'tier3',
  'serrania-de-ronda': 'tier3',
};

export type PricingData = {
  cinta: number;
  motorReparar: number;
  motorSustituir: number;
  lamasPorLama: number;
  desplazamiento: string;
  note: string;
};

export const PRICING_PERSIANAS: Record<PricingTier, PricingData> = {
  tier1: {
    cinta: 60,
    motorReparar: 80,
    motorSustituir: 150,
    lamasPorLama: 15,
    desplazamiento: 'Incluido',
    note: 'Sin coste de desplazamiento dentro del término municipal.',
  },
  tier2: {
    cinta: 65,
    motorReparar: 85,
    motorSustituir: 155,
    lamasPorLama: 15,
    desplazamiento: '+5€',
    note: 'Desplazamiento incluido si reparación supera 80€.',
  },
  tier3: {
    cinta: 75,
    motorReparar: 95,
    motorSustituir: 165,
    lamasPorLama: 15,
    desplazamiento: '+10-15€',
    note: 'Visita agrupada con otros clientes de la zona para optimizar costes.',
  },
};

export function getPricingForCity(slug: string): PricingData {
  const tier = PRICING_TIER_MAP[slug] || 'tier2';
  return PRICING_PERSIANAS[tier];
}

// ============================================================
// BACKWARD COMPATIBILITY — wrappers for toldos & mosquiteras
// ============================================================

interface TarifaItem {
  servicio: string;
  precio: string;
  icon: string;
}

interface TarifaToldoItem {
  tipo: string;
  precio: string;
}

interface TarifaMosqItem {
  tipo: string;
  precio: string;
}

function oldTypeToTier(type: string): PricingTier {
  if (type === 'balneario') return 'tier1';
  if (type === 'interior') return 'tier2';
  return 'tier3';
}

const basePersianas: TarifaItem[] = [
  { servicio: "Cambiar cinta de persiana", precio: "60 €", icon: "🪢" },
  { servicio: "Reparar motor existente", precio: "80 €", icon: "🔧" },
  { servicio: "Sustituir motor completo", precio: "150 €", icon: "⚡" },
  { servicio: "Cambiar lama individual", precio: "15 €", icon: "📐" },
  { servicio: "Motorizar persiana manual", precio: "200 €", icon: "🆕" },
  { servicio: "Visita técnica + diagnóstico", precio: "GRATIS", icon: "🔍" },
];

export function getCityTarifasPersianas(slug: string, _name: string): TarifaItem[] {
  const type = getCityType(slug);
  const tier = oldTypeToTier(type);
  const prices = PRICING_PERSIANAS[tier];

  const clone = basePersianas.map((t) => ({ ...t }));
  clone[0].precio = `${prices.cinta} €`;
  clone[1].precio = `${prices.motorReparar} €`;
  clone[2].precio = `${prices.motorSustituir} €`;
  clone[3].precio = `${prices.lamasPorLama} €`;

  if (tier === 'tier2') clone[4].precio = "210 €";
  if (tier === 'tier3') clone[4].precio = "215 €";

  return clone;
}

const baseToldos: TarifaToldoItem[] = [
  { tipo: "🏠 Toldo balcón manual", precio: "350 €" },
  { tipo: "🌅 Toldo extensible 3x2m", precio: "600 €" },
  { tipo: "⚡ Toldo motorizado 4x3m", precio: "1.200 €" },
  { tipo: "📦 Toldo cofre completo", precio: "1.500 €" },
  { tipo: "🌿 Pérgola bioclimática", precio: "4.000 €" },
  { tipo: "🔍 Visita técnica + diseño", precio: "GRATIS" },
];

export function getCityTarifasToldos(slug: string, _name: string): TarifaToldoItem[] {
  const type = getCityType(slug);
  const tier = oldTypeToTier(type);
  const clone = baseToldos.map((t) => ({ ...t }));
  if (tier === 'tier2') {
    clone[1].precio = "650 €";
    clone[2].precio = "1.250 €";
    clone[3].precio = "1.550 €";
    return clone;
  }
  if (tier === 'tier3') {
    clone[1].precio = "675 €";
    clone[2].precio = "1.300 €";
    clone[3].precio = "1.600 €";
    return clone;
  }
  return clone;
}

const baseMosquiteras: TarifaMosqItem[] = [
  { tipo: "🪟 Mosquitera fija ventana pequeña", precio: "60 €" },
  { tipo: "🚪 Mosquitera corredera puerta", precio: "120 €" },
  { tipo: "📜 Mosquitera enrollable", precio: "150 €" },
  { tipo: "🦟 Mosquitera plisada", precio: "180 €" },
  { tipo: "🔧 Reparación (cambio tela)", precio: "35 €" },
  { tipo: "🔍 Visita técnica + presupuesto", precio: "GRATIS" },
];

export function getCityTarifasMosquiteras(slug: string, _name: string): TarifaMosqItem[] {
  const type = getCityType(slug);
  const tier = oldTypeToTier(type);
  const clone = baseMosquiteras.map((t) => ({ ...t }));
  if (tier === 'tier2') {
    clone[0].precio = "65 €";
    clone[1].precio = "125 €";
    clone[2].precio = "160 €";
    return clone;
  }
  if (tier === 'tier3') {
    clone[0].precio = "70 €";
    clone[1].precio = "135 €";
    clone[2].precio = "170 €";
    clone[4].precio = "40 €";
    return clone;
  }
  return clone;
}